import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import {
  TOOL_REGISTRY,
  DEFAULT_TOOL_ORDER,
  getCustomToolKey,
  getCustomToolIcon,
} from '@/config/tools';

// Re-export for backward compatibility with pages that import from here
// TODO: Update pages to import directly from @/config/tools
export const toolConfigs = TOOL_REGISTRY;

/**
 * Custom hook to fetch space/project data using optimized RPC
 *
 * @param {string} projectId - The project/space ID
 * @param {string} userId - The current user's ID (for tool order preferences)
 * @param {string} currentCompanyId - The user's currently active company ID (for access validation)
 * @returns {object} { space, spaceTools, teamMembers, accessUsers, toolOrder, toolPreviews, loading, error, accessDenied, refetch }
 */
export const useSpaceData = (projectId, userId, currentCompanyId = null) => {
  const [space, setSpace] = useState(null);
  const [spaceTools, setSpaceTools] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [accessUsers, setAccessUsers] = useState({ admins: [], managers: [] });
  const [toolOrder, setToolOrder] = useState([]);
  const [toolPreviews, setToolPreviews] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const loadSpace = useCallback(async () => {
    if (!projectId) {
      setLoading(false);
      return;
    }

    // Reset access denied state on new load
    setAccessDenied(false);

    try {
      // Fetch from optimized RPC
      const { data, error: fetchError } = await supabase.rpc('get_space_page_data', {
        p_project_id: projectId,
        p_user_id: userId || '00000000-0000-0000-0000-000000000000'
      });

      if (fetchError) throw fetchError;

      if (data?.error) {
        throw new Error(data.error);
      }

      const projectData = data?.project;
      if (!projectData) {
        throw new Error('Project not found');
      }

      // Transform project data
      const transformedProject = {
        id: projectData.id,
        name: projectData.name,
        description: projectData.description,
        location: projectData.location,
        location_data: projectData.location_data || null,
        industry: projectData.industry,
        type: projectData.type,
        avatar: projectData.avatar || null,
        status: 'On track',
        lastUpdated: `Updated ${new Date(projectData.updated_date).toLocaleDateString()}`,
        visibility_type: projectData.visibility_type,
        tools: typeof projectData.tools === 'string'
          ? JSON.parse(projectData.tools)
          : projectData.tools || {},
        schedule: typeof projectData.schedule === 'string'
          ? JSON.parse(projectData.schedule)
          : projectData.schedule || {},
        custom_fields: projectData.custom_fields || {},
        company_id: projectData.company_id,
        access_control: projectData.access_control,
        assignments: 0
      };

      // Check if the space belongs to the user's current company
      // If currentCompanyId is provided and doesn't match, set access denied
      if (currentCompanyId && transformedProject.company_id && transformedProject.company_id !== currentCompanyId) {
        console.warn('useSpaceData: Space belongs to different company. Current:', currentCompanyId, 'Space:', transformedProject.company_id);
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      // Process tools from the space
      let tools = [];
      if (transformedProject.tools) {
        // Add built-in tools
        tools = Object.entries(transformedProject.tools)
          .filter(([key, enabled]) => enabled === true && key !== '_custom')
          .map(([toolKey]) => ({
            id: toolKey,
            tool_type: toolKey,
            name: toolConfigs[toolKey]?.name || toolKey,
            color: toolConfigs[toolKey]?.color,
            icon: toolConfigs[toolKey]?.icon,
            isCustom: false
          }));

        // Add custom tools from _custom array
        const customTools = transformedProject.tools._custom || [];
        customTools
          .filter(ct => ct.enabled)
          .forEach(customTool => {
            const toolKey = getCustomToolKey(customTool.id);
            tools.push({
              id: toolKey,
              tool_type: toolKey,
              name: customTool.name,
              color: customTool.color || '#6366f1',
              icon: getCustomToolIcon(customTool.icon),
              isCustom: true,
              customToolData: customTool
            });
          });
      }

      // Format team members
      const formattedMembers = (data.team_members || []).map(member => ({
        id: member.id || member.user_id,
        user_id: member.user_id || member.id,
        name: `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email,
        email: member.email,
        avatar: member.avatar_url || null,
        role: member.role,
        status: member.status,
        department: member.department,
        compliance_percentage: member.compliance_percentage,
        initials: `${(member.first_name || member.email || '')[0]}${(member.last_name || '')[0] || ''}`.toUpperCase()
      }));

      // Format access users
      const formattedAccessUsers = {
        admins: (data.access_users?.admins || []).map(admin => ({
          id: admin.id,
          name: `${admin.first_name || ''} ${admin.last_name || ''}`.trim() || admin.email,
          email: admin.email,
          avatar: admin.avatar_url || null,
          initials: `${(admin.first_name || admin.email || '')[0]}${(admin.last_name || '')[0] || ''}`.toUpperCase()
        })),
        managers: (data.access_users?.managers || []).map(manager => ({
          id: manager.id,
          name: `${manager.first_name || ''} ${manager.last_name || ''}`.trim() || manager.email,
          email: manager.email,
          avatar: manager.avatar_url || null,
          initials: `${(manager.first_name || manager.email || '')[0]}${(manager.last_name || '')[0] || ''}`.toUpperCase()
        }))
      };

      // Calculate tool order
      // Only include tools that are both enabled AND have a valid config (exclude _custom key)
      const enabledToolKeys = Object.entries(transformedProject.tools)
        .filter(([key, enabled]) => enabled && toolConfigs[key] && key !== '_custom')
        .map(([key]) => key);

      // Also include custom tool keys in the order
      const customToolKeys = (transformedProject.tools._custom || [])
        .filter(ct => ct.enabled)
        .map(ct => getCustomToolKey(ct.id));

      let calculatedToolOrder;
      const userToolOrder = data.user_tool_order;
      const allToolKeys = [...enabledToolKeys, ...customToolKeys];

      if (userToolOrder && userToolOrder.length > 0) {
        // Use saved user-specific order, filtering out any tools without configs (allow custom tools)
        const validOrder = userToolOrder.filter(key =>
          (enabledToolKeys.includes(key) && toolConfigs[key]) || customToolKeys.includes(key)
        );
        const missingTools = allToolKeys.filter(key => !validOrder.includes(key));
        calculatedToolOrder = [...validOrder, ...missingTools];
      } else {
        // No saved preferences, use default order for built-in tools, then add custom tools at end
        const orderedTools = DEFAULT_TOOL_ORDER.filter(key => enabledToolKeys.includes(key));
        const missingBuiltInTools = enabledToolKeys.filter(key => !DEFAULT_TOOL_ORDER.includes(key));
        calculatedToolOrder = [...orderedTools, ...missingBuiltInTools, ...customToolKeys];
      }

      // Update state
      setSpace(transformedProject);
      setSpaceTools(tools);
      setTeamMembers(formattedMembers);
      setAccessUsers(formattedAccessUsers);
      setToolOrder(calculatedToolOrder);
      // Normalize preview keys: RPC returns 'checklists' but new spaces use 'tasks'
      const previews = data.tool_previews || {};
      if (previews.checklists && !previews.tasks) {
        previews.tasks = previews.checklists;
      }
      setToolPreviews(previews);
      setError(null);
    } catch (err) {
      console.error('Error loading space:', err);
      setError(err);
      toast.error('Failed to load space details');
    } finally {
      setLoading(false);
    }
  }, [projectId, userId, currentCompanyId]);

  // Save tool order when it changes
  const saveToolOrder = useCallback(async (newOrder) => {
    if (!projectId || !userId) return;

    setToolOrder(newOrder);

    try {
      await supabase.from('user_space_preferences').upsert(
        {
          user_id: userId,
          project_id: projectId,
          tool_order: newOrder,
          updated_date: new Date().toISOString()
        },
        {
          onConflict: 'user_id,project_id'
        }
      );
    } catch (error) {
      console.error('Error saving tool order:', error);
    }
  }, [projectId, userId]);

  useEffect(() => {
    loadSpace();
  }, [loadSpace]);

  return {
    space,
    spaceTools,
    teamMembers,
    accessUsers,
    toolOrder,
    toolPreviews,
    loading,
    error,
    accessDenied,
    refetch: loadSpace,
    saveToolOrder
  };
};
